import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AppBar, Toolbar, Typography, Container, Button, Box, IconButton, Menu, MenuItem, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import theme, { brand } from './theme';
import { API_KEYS_URL, linkProps } from './links';
import Home from './components/Home';
import ApiDocs from './components/ApiDocs';
import Contact from './components/Contact';
import Footer from './components/Footer';
import FAQ from './components/FAQ';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'API Docs', to: '/docs' },
  { label: 'Get API Key', to: API_KEYS_URL },
  { label: 'Contact', to: '/contact' },
];

function NavBar() {
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:900px)');
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <AppBar position="sticky" elevation={0}>
      <Toolbar sx={{ maxWidth: 1240, width: '100%', mx: 'auto', py: 1 }}>
        <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexGrow: 1 }}>
          <img src="/logo.png" alt="Arc Light Technologies" style={{ height: 34, width: 34, marginRight: 10 }} />
          <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '1.0625rem', letterSpacing: '-0.02em', color: brand.fg }}>
            Arc Light <Box component="span" sx={{ color: brand.primary }}>API</Box>
          </Typography>
        </Box>

        {isMobile ? (
          <>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: brand.fg, border: `1px solid ${brand.border}`, borderRadius: '10px' }}>
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{ sx: { bgcolor: brand.s1, border: `1px solid ${brand.border}`, borderRadius: '14px', mt: 1 } }}
            >
              {NAV_LINKS.map((link) => (
                <MenuItem key={link.to} {...linkProps(link.to)} onClick={() => setAnchorEl(null)} sx={{ color: brand.fg, fontFamily: "'Outfit', sans-serif" }}>
                  {link.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {NAV_LINKS.map((link) => {
              const active = location.pathname === link.to;
              const isCta = link.to === API_KEYS_URL;
              return (
                <Button
                  key={link.to}
                  {...linkProps(link.to)}
                  variant={isCta ? 'outlined' : 'text'}
                  sx={{
                    color: active && !isCta ? brand.fg : undefined,
                    bgcolor: active && !isCta ? 'rgba(255,255,255,0.06)' : undefined,
                    ml: isCta ? 1.5 : 0,
                  }}
                >
                  {link.label}
                </Button>
              );
            })}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Key requests and management moved to the SleuthNet profile page; keep the
// old paths working for bookmarks and links in emails.
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <NavBar />
          <Container sx={{ mt: 5, mb: 6, flexGrow: 1 }}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/docs" element={<ApiDocs />} />
              <Route path="/api-key" element={<ExternalRedirect to={API_KEYS_URL} />} />
              <Route path="/account" element={<ExternalRedirect to={API_KEYS_URL} />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
            </Routes>
          </Container>
          <Footer />
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
